// @flow

import { useMemo, useEffect, useState } from 'react';
import { usePosition } from 'use-position';
import { orderByDistance } from 'geolib';
import VenueMap from '../VenueMap';
import VenueList from '../VenueList';
import MapFilter from '../MapFilter';

import type { Venue, CenterType } from '../types';

import './index.scss';

const VenueCollectionContainer = ({ venues }: { venues: Array<Venue> }) => {
  const { latitude, longitude, timestamp, accuracy, error } = usePosition();
  const haveLatLng = useMemo(() => !!latitude && !!longitude, [
    latitude,
    longitude,
  ]);

  const [venuesByDistance, setVenuesByDistance] = useState([]);
  useEffect(() => {
    const newVenues = haveLatLng
      ? orderByDistance({ latitude, longitude }, venues)
      : orderByDistance({ latitude: 39, longitude: -77 }, venues);
    if (!newVenues || newVenues.length === 0) return;
    setVenuesByDistance(newVenues);
  }, [haveLatLng, venues]);

  const defaultCenter = useMemo(
    () =>
      haveLatLng && venuesByDistance.length > 0
        ? {
            lat: venuesByDistance[0].latitude,
            lng: venuesByDistance[0].longitude,
          }
        : undefined,
    [haveLatLng, venuesByDistance],
  );

  const calculateZoom = (zoom, autofocus = true) => {
    if (autofocus) {
      return 10;
    }
    if (zoom) return zoom;
    return haveLatLng || center ? 6 : 4;
  };

  const [center: CenterType | void, setCenter] = useState(defaultCenter);
  const [activeCenter: CenterType | void, setActiveCenter] = useState(null);
  const [location: CenterType | void, setLocation] = useState(null);
  const [zoom: Number, setZoom] = useState(6);
  useEffect(() => {
    if (activeCenter && activeCenter.lat && activeCenter.lng)
      setVenuesByDistance(
        orderByDistance(
          { latitude: activeCenter.lat, longitude: activeCenter.lng },
          venues,
        ),
      );
  }, [activeCenter]);

  const syncCenterClicked = (
    center: CenterType | null,
    forceMapUpdate: ?boolean,
  ) => {
    setActiveCenter(center);
    setZoom(calculateZoom(null, true));
    if (process.env.NODE_ENV === 'development')
      console.log('setActiveCenter ', center);

    if (center || forceMapUpdate) {
      setCenter(center);

      if (process.env.NODE_ENV === 'development')
        console.log('setCenter ', center);
    }
  };

  const onZoomChange = zoomDelta => {
    setZoom(zoom + zoomDelta);
  };

  const onDragEnd = map => {
    setActiveCenter(null);
    if (!map.center) return;
    const lat = map.center.lat();
    const lng = map.center.lng();
    if (!lat || !lng) {
      console.error('Impossibile recuperare la posizione attuale sulla mappa');
      return;
    }
    setVenuesByDistance(
      orderByDistance(
        {
          latitude: lat,
          longitude: lng,
        },
        venues,
      ),
    );
  };

  const currentPosition = location
    ? location
    : haveLatLng
    ? { lat: latitude, lng: longitude }
    : undefined;

  return (
    <div className="flex-container">
      <div className="map-column">
        <div className="legend">
          Military installation. Public access may be limited. Contact center
          for details.
        </div>
        <VenueMap
          center={center ? center : location ? location : defaultCenter}
          zoom={zoom}
          venues={venuesByDistance}
          onMarkerClick={syncCenterClicked}
          onDragEnd={onDragEnd}
          onZoomChange={onZoomChange}
        />
      </div>
      <div className="filters-list-column">
        <MapFilter
          onVenueSelected={center => {
            syncCenterClicked(center, true);
            setLocation(center);
          }}
          placeholder="Search..."
        />
        <VenueList
          myPosition={currentPosition}
          activeCenter={activeCenter}
          setActiveCenter={syncCenterClicked}
          venues={venuesByDistance}
        />
      </div>
    </div>
  );
};

export default VenueCollectionContainer;
